import { Box, Card, Heading, SimpleGrid, Spinner, Text, VStack } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'
import {
  ActivityIcon,
  BotIcon,
  BoxesIcon,
  CarIcon,
  EyeIcon,
  FactoryIcon,
  FuelIcon,
  GlobeIcon,
  InfoIcon,
  LayersIcon,
  LockIcon,
  NetworkIcon,
  PillIcon,
  PlayIcon,
  RadioIcon,
  ServerIcon,
  SparklesIcon,
  TerminalIcon,
  TruckIcon,
  ZapIcon,
} from 'lucide-react'
import { useEffect } from 'react'
import CustomerCard from '@/components/CustomerCard'
import Marquee from '@/components/magicui/marquee'
import { InsightCard, PageHeader, SectionHeading } from '@/components/shell'
import { customersApi } from '@/lib/api'

export const Route = createFileRoute('/_app/')({
  component: AppHome,
})

interface FeaturedInsight {
  title: string
  badgeLabel: string
  description: string
  route: string
  actionLabel: string
  icon: LucideIcon
  accent: string
  colorPalette: string
}

const featured: FeaturedInsight[] = [
  {
    title: 'Discover HiveMQ',
    badgeLabel: 'HiveMQ',
    description: 'The Industrial Data Platform for Agentic AI',
    route: '/hivemqintro',
    actionLabel: 'Get Started',
    icon: SparklesIcon,
    accent: 'brand.solid',
    colorPalette: 'yellow',
  },
  {
    title: 'HiveMQ Vision',
    badgeLabel: 'Vision',
    description: 'From streaming to intelligence to agentic operations',
    route: '/hivemqvision',
    actionLabel: 'Our Vision',
    icon: EyeIcon,
    accent: 'brand.solid',
    colorPalette: 'yellow',
  },
  {
    title: 'Data Intelligence',
    badgeLabel: 'Intelligence',
    description:
      'Powered by HiveMQ Pulse - build your Unified Namespace and turn it into distributed data intelligence',
    route: '/pulse',
    actionLabel: 'Explore Data Intelligence',
    icon: ActivityIcon,
    accent: 'brand.solid',
    colorPalette: 'yellow',
  },
  {
    title: 'Agentic AI',
    badgeLabel: 'Agentic AI',
    description: 'Safe, governed closed-loop AI acting on your real-time operational data',
    route: '/hivemqagentic',
    actionLabel: 'Explore Agentic AI',
    icon: BotIcon,
    accent: 'brand.solid',
    colorPalette: 'yellow',
  },
]

interface ToolInsight {
  title: string
  description: string
  route: string
  actionLabel: string
  icon: LucideIcon
  accent: string
  colorPalette: string
}

// Kept in sync with the Tools pillar submenu in navConfig.ts
// (Playground / MQTT Client / Demo Info).
const tools: ToolInsight[] = [
  {
    title: 'Playground',
    description: 'Interactive MQTT testing environment',
    route: '/playground',
    actionLabel: 'Open Playground',
    icon: PlayIcon,
    accent: 'blue.solid',
    colorPalette: 'blue',
  },
  {
    title: 'MQTT Client',
    description: 'Connect, publish, and subscribe from a browser-based MQTT client',
    route: '/mqtt-client',
    actionLabel: 'Open MQTT Client',
    icon: TerminalIcon,
    accent: 'purple.solid',
    colorPalette: 'purple',
  },
  {
    title: 'Demo Info',
    description: 'Endpoints and credentials for the live demo environment',
    route: '/demoinfo',
    actionLabel: 'View Demo Info',
    icon: InfoIcon,
    accent: 'orange.solid',
    colorPalette: 'orange',
  },
]

interface LearnInsight {
  title: string
  description: string
  route: string
  actionLabel: string
  icon: LucideIcon
  accent: string
  colorPalette: string
}

// Learning + exploration pages (the Learn pillar's education topics, plus the
// interactive Value Space globe).
const learn: LearnInsight[] = [
  {
    title: 'MQTT',
    description: 'The fundamentals of the MQTT messaging protocol',
    route: '/education/mqtt',
    actionLabel: 'Learn MQTT',
    icon: RadioIcon,
    accent: 'blue.solid',
    colorPalette: 'blue',
  },
  {
    title: 'Sparkplug',
    description: 'MQTT Sparkplug B for plug-and-play industrial interoperability',
    route: '/education/sparkplug',
    actionLabel: 'Learn Sparkplug',
    icon: ZapIcon,
    accent: 'green.solid',
    colorPalette: 'green',
  },
  {
    title: 'Unified Namespace',
    description: 'Organize operational data with a UNS',
    route: '/education/uns',
    actionLabel: 'Learn UNS',
    icon: LayersIcon,
    accent: 'purple.solid',
    colorPalette: 'purple',
  },
  {
    title: 'HiveMQ Cluster',
    description: 'How the HiveMQ broker clusters for scale and high availability',
    route: '/education/hivemq-cluster',
    actionLabel: 'Learn Clustering',
    icon: BoxesIcon,
    accent: 'teal.solid',
    colorPalette: 'teal',
  },
  {
    title: 'TLS & mTLS',
    description: 'How the broker and clients authenticate with keystores and truststores',
    route: '/education/tls-mtls',
    actionLabel: 'Learn TLS',
    icon: LockIcon,
    accent: 'red.solid',
    colorPalette: 'red',
  },
  {
    title: 'Value Space',
    description: 'Explore global IoT deployment scenarios on an interactive globe',
    route: '/valuespace',
    actionLabel: 'Explore',
    icon: GlobeIcon,
    accent: 'cyan.solid',
    colorPalette: 'cyan',
  },
]

interface UseCaseInsight {
  title: string
  subtitle: string
  description: string
  route: string
  icon: LucideIcon
  accent: string
  colorPalette: string
  /** Idle animation for the card's decorative accent icon (see InsightCard). */
  animation: string
}

const useCases: UseCaseInsight[] = [
  {
    title: 'Factory',
    subtitle: 'BBCorp',
    description: 'Digital transformation for smart manufacturing',
    route: '/factory',
    icon: FactoryIcon,
    accent: 'orange.solid',
    colorPalette: 'orange',
    animation: 'pulse',
  },
  {
    title: 'Automotive',
    subtitle: 'CONCAR',
    description: 'Connected vehicles and fleet management',
    route: '/automotive',
    icon: CarIcon,
    accent: 'blue.solid',
    colorPalette: 'blue',
    animation: 'drift',
  },
  {
    title: 'Energy',
    subtitle: 'WINDREN',
    description: 'Smart grids and renewable energy',
    route: '/energy',
    icon: ZapIcon,
    accent: 'green.solid',
    colorPalette: 'green',
    animation: 'flash',
  },
  {
    title: 'Pharma',
    subtitle: 'BIOMED',
    description: 'Pharmaceutical manufacturing solutions',
    route: '/pharma',
    icon: PillIcon,
    accent: 'purple.solid',
    colorPalette: 'purple',
    animation: 'bob',
  },
  {
    title: 'Logistics',
    subtitle: 'HPS',
    description: 'Supply chain and delivery optimization',
    route: '/logistics',
    icon: TruckIcon,
    accent: 'teal.solid',
    colorPalette: 'teal',
    animation: 'drift',
  },
  {
    title: 'Oil & Gas',
    subtitle: 'OG CORP',
    description: 'Pipeline monitoring and asset management',
    route: '/oilandgas',
    icon: FuelIcon,
    accent: 'red.solid',
    colorPalette: 'red',
    animation: 'pulse',
  },
  {
    title: 'Connected Assets',
    subtitle: 'HTC',
    description: 'IoT device management at scale',
    route: '/connected',
    icon: NetworkIcon,
    accent: 'cyan.solid',
    colorPalette: 'cyan',
    animation: 'pulse',
  },
  {
    title: 'Data Center',
    subtitle: 'NOVATECH',
    description: 'Infrastructure monitoring and operations',
    route: '/datacenter',
    icon: ServerIcon,
    accent: 'pink.solid',
    colorPalette: 'pink',
    animation: 'bob',
  },
]

function AppHome() {
  const navigate = useNavigate()

  // Fetch customers from API
  const { data: apiCustomers, isLoading: isLoadingCustomers } = useQuery({
    queryKey: ['customers', 'home'],
    queryFn: () => customersApi.getForHome(),
  })

  // Prefetch heavy modules in the background for faster navigation
  useEffect(() => {
    // Use requestIdleCallback to load during browser idle time
    const prefetchModules = () => {
      // Prefetch the globe module (1.2MB) for Value Space page
      import('react-globe.gl').catch(() => {
        // Silently ignore prefetch errors
      })
    }

    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(prefetchModules, { timeout: 3000 })
      return () => window.cancelIdleCallback(id)
    } else {
      // Fallback: load after a short delay
      const timer = setTimeout(prefetchModules, 2000)
      return () => clearTimeout(timer)
    }
  }, [])

  // Split customers into two rows for the marquee
  const customers = apiCustomers || []
  const firstRow = customers.slice(0, Math.ceil(customers.length / 2))
  const secondRow = customers.slice(Math.ceil(customers.length / 2))

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="Discover us"
        subtitle="Explore the HiveMQ platform - its vision, its data intelligence layer, and hands-on demos across real-world industries and tools."
      />

      <VStack align="stretch" gap={10}>
        {/* Featured entry points */}
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={6}>
          {featured.map((item) => (
            <InsightCard
              key={item.title}
              accent={item.accent}
              colorPalette={item.colorPalette}
              icon={item.icon}
              badgeLabel={item.badgeLabel}
              title={item.title}
              description={item.description}
              actionLabel={item.actionLabel}
              onClick={() => navigate({ to: item.route })}
            />
          ))}
        </SimpleGrid>

        {/* Industry use cases */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Industry Use Cases"
            description="Explore how HiveMQ powers IoT solutions across different industries"
          />
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} gap={6}>
            {useCases.map((useCase) => (
              <InsightCard
                key={useCase.title}
                accent={useCase.accent}
                colorPalette={useCase.colorPalette}
                icon={useCase.icon}
                iconAnimation={useCase.animation}
                badgeLabel="Industry"
                title={useCase.title}
                description={useCase.description}
                tag={useCase.subtitle}
                actionLabel="Explore"
                onClick={() => navigate({ to: useCase.route })}
              />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Learn & explore */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Learn & Explore"
            description="Guides and interactive explorations to go deeper on the technology"
          />
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap={6}>
            {learn.map((item) => (
              <InsightCard
                key={item.title}
                accent={item.accent}
                colorPalette={item.colorPalette}
                icon={item.icon}
                badgeLabel="Learn"
                title={item.title}
                description={item.description}
                actionLabel={item.actionLabel}
                onClick={() => navigate({ to: item.route })}
              />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Tools & resources */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Tools & Resources"
            description="Interactive tools to explore and experiment with HiveMQ features"
          />
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap={6}>
            {tools.map((tool) => (
              <InsightCard
                key={tool.title}
                accent={tool.accent}
                colorPalette={tool.colorPalette}
                icon={tool.icon}
                badgeLabel="Tool"
                title={tool.title}
                description={tool.description}
                actionLabel={tool.actionLabel}
                onClick={() => navigate({ to: tool.route })}
              />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Customer success marquee */}
        <VStack align="stretch" gap={6}>
          <VStack gap={2} textAlign="center">
            <Text
              fontSize="sm"
              fontWeight="bold"
              color="brand.solid"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              Customer Success
            </Text>
            <Heading size="xl">Trusted by Industry Leaders</Heading>
            <Text color="content.secondary" maxW="600px">
              Join hundreds of enterprises worldwide who rely on HiveMQ for their mission-critical
              IoT infrastructure
            </Text>
          </VStack>
          <Card.Root overflow="hidden" bg="bg.subtle">
            <Card.Body py={8} px={0} position="relative">
              {isLoadingCustomers ? (
                <VStack py={8}>
                  <Spinner size="lg" />
                  <Text color="content.secondary">Loading customers...</Text>
                </VStack>
              ) : customers.length === 0 ? (
                <Text textAlign="center" py={8} color="content.secondary">
                  No customers to display
                </Text>
              ) : (
                <VStack gap={6} position="relative">
                  {/* Fade the marquee into the panel at both edges */}
                  <Box
                    position="absolute"
                    insetY={0}
                    left={0}
                    w={{ base: '40px', md: '96px' }}
                    zIndex={1}
                    pointerEvents="none"
                    css={{
                      background: 'linear-gradient(to right, {colors.bg.subtle}, transparent)',
                    }}
                  />
                  <Box
                    position="absolute"
                    insetY={0}
                    right={0}
                    w={{ base: '40px', md: '96px' }}
                    zIndex={1}
                    pointerEvents="none"
                    css={{
                      background: 'linear-gradient(to left, {colors.bg.subtle}, transparent)',
                    }}
                  />
                  <Marquee pauseOnHover duration="30s">
                    {firstRow.map((customer) => (
                      <CustomerCard
                        key={customer.id}
                        name={customer.name}
                        industry={customer.industry}
                        img={customer.logoUrl}
                        link=""
                        slug={customer.slug}
                      />
                    ))}
                  </Marquee>
                  <Marquee reverse pauseOnHover duration="30s">
                    {secondRow.map((customer) => (
                      <CustomerCard
                        key={customer.id}
                        name={customer.name}
                        industry={customer.industry}
                        img={customer.logoUrl}
                        link=""
                        slug={customer.slug}
                      />
                    ))}
                  </Marquee>
                </VStack>
              )}
            </Card.Body>
          </Card.Root>
        </VStack>
      </VStack>
    </Box>
  )
}
